import React from "react";

interface FormInputAttributes {
  required?: boolean;
  inputmode?: string;
  [key: string]: unknown;
}

interface FormInputProps {
  placeholder?: string;
  type?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  required?: boolean;
  attributes?: FormInputAttributes;
}

/*
 * Override for builder.io/react's built-in "Form:Input" component.
 *
 * Fixes two builder.io v8 SSR issues:
 * 1. `required` mismatch: the SDK passes `required` via both `props.required` and
 *    `props.attributes`, and the two sources disagree between server and client.
 *    We resolve them explicitly so both renders agree.
 * 2. `inputmode` attribute: the SDK passes the raw HTML attribute name as a React
 *    prop, which React rejects with a warning. We convert it to camelCase `inputMode`.
 */
export default function FormInput({
  placeholder,
  type,
  name,
  value,
  defaultValue,
  required,
  attributes,
}: FormInputProps) {
  const {
    inputmode,
    required: attrRequired,
    ...restAttributes
  } = attributes ?? {};

  const resolvedRequired = Boolean(required ?? attrRequired ?? false);

  const valueProps = value === undefined ? { defaultValue } : { value };

  return (
    <input
      placeholder={placeholder}
      type={type}
      name={name}
      {...valueProps}
      required={resolvedRequired}
      inputMode={
        inputmode as React.HTMLAttributes<HTMLInputElement>["inputMode"]
      }
      {...(restAttributes as React.InputHTMLAttributes<HTMLInputElement>)}
      // Builder.io v8 passes `required` differently on the server vs the client
      // (a known SDK bug). The server HTML is correct; React just can't reconcile
      // the two virtual DOM representations. suppressHydrationWarning tells React
      // to accept the server HTML and use the client value after hydration.
      suppressHydrationWarning
    />
  );
}
